import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Grid, Hidden } from '@mui/material';
import { MarketInputText } from '@market/react';
import ControlledInputText from 'components/common/forms/controlled_input_text';
import ControlledInputSelect from 'components/common/forms/controlled_input_select';
import ControlledCheckbox from 'components/common/forms/controlled_checkbox';
import { AU_STATE_MAPPINGS } from 'constants/mappings';
import { BIRTHDATE_REGEX, EQUIFAX_PATTERNS } from 'constants/patterns';
import { buildLegalLink } from 'utilities/links';
import { DATADOG_PRIVACY_LEVELS } from 'constants/enums';

const Fields = (props) => {
  const { applicationData, disabled } = props;
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <>
      {/* this is a placeholder element!*/}
      <Grid item xs={12}>
        <MarketInputText
          name="account_holder_name"
          readonly
          value={[
            applicationData.personal_first_name,
            applicationData.personal_last_name,
          ].join(' ')}
          disabled
        >
          <label>Account holder name</label>
        </MarketInputText>
      </Grid>
      <Hidden implementation="css" xsUp>
        <Grid item xs={12} md={6}>
          <ControlledInputText
            label="First name"
            name="personal_first_name"
            control={control}
            maxlength={40}
            rules={{
              required: true,
              maxLength: 40,
              pattern: EQUIFAX_PATTERNS.name,
            }}
            disabled
            defaultValue={applicationData.personal_first_name || ''}
            autocomplete="given-name"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledInputText
            label="Last name"
            name="personal_last_name"
            control={control}
            maxlength={50}
            rules={{
              required: true,
              maxLength: 50,
              pattern: EQUIFAX_PATTERNS.name,
            }}
            disabled
            defaultValue={applicationData.personal_last_name || ''}
            autocomplete="family-name"
          />
        </Grid>
      </Hidden>
      <Grid item xs={12} md={6}>
        <ControlledInputText
          label="Date of birth"
          name="personal_birthdate"
          control={control}
          disabled={disabled}
          maxlength={10}
          rules={{
            required: true,
            pattern: BIRTHDATE_REGEX,
            maxLength: 10,
          }}
          defaultValue={applicationData.personal_birthdate || ''}
          placeholder="YYYY-MM-DD"
        />
      </Grid>
      <Grid item xs={12} md={6} data-dd-privacy={DATADOG_PRIVACY_LEVELS.HIDDEN}>
        <ControlledInputSelect
          label="Gender"
          name="personal_gender_code"
          control={control}
          disabled={disabled}
          rules={{
            required: true,
          }}
          defaultValue={applicationData.personal_gender_code || ''}
          options={[
            { value: 'M', displayValue: 'Male' },
            { value: 'F', displayValue: 'Female' },
            { value: 'U', displayValue: 'Prefer not to say' },
          ]}
        />
      </Grid>
      <Grid item xs={12}>
        <ControlledInputText
          label="Home address"
          name="personal_street_1"
          control={control}
          disabled={disabled}
          rules={{
            required: true,
            pattern: EQUIFAX_PATTERNS.street,
          }}
          defaultValue={applicationData.personal_street_1 || ''}
          autocomplete="address-line1"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ControlledInputText
          label="Suburb"
          name="personal_city"
          defaultValue={applicationData.personal_city || ''}
          control={control}
          disabled={disabled}
          maxlength={50}
          rules={{
            required: true,
            maxLength: 50,
            pattern: EQUIFAX_PATTERNS.city,
          }}
          autocomplete="address-level2"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ControlledInputText
          label="State/Territory"
          name="personal_state_province"
          disabled={disabled}
          defaultValue={applicationData.personal_state_province || ''}
          control={control}
          rules={{
            required: true,
            pattern: new RegExp(
              `^(${Object.keys(AU_STATE_MAPPINGS).join('|')})$`,
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ControlledInputText
          label="Postcode"
          name="personal_postal_code"
          disabled={disabled}
          defaultValue={applicationData.personal_postal_code || ''}
          control={control}
          maxlength={4}
          rules={{
            required: true,
            maxLength: 4,
          }}
        />
      </Grid>
      <Grid item xs={12} md={12} mb={2}>
        <ControlledCheckbox
          name="accepted_legal_agreements"
          control={control}
          disabled={disabled}
          label={
            <div
              dangerouslySetInnerHTML={{
                __html: t('checkout.financeApp.legal_agreement_html', {
                  privacyLink: buildLegalLink('/privacy'),
                  esignLink: buildLegalLink('/sign'),
                  creditLink: buildLegalLink('/shop-credit-authorization'),
                }),
              }}
            />
          }
          defaultValue={applicationData.accepted_legal_agreements || false}
          rules={{ required: true }}
        />
      </Grid>
    </>
  );
};

export default Fields;
